import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, ExternalLink } from "lucide-react";
import { TRADITIONS } from "@/data/content";
import { DEEP, TRADITION_SOURCES } from "@/data/traditionDeep";
import { Reveal, Eyebrow } from "@/components/common";

export default function TraditionDetail() {
  const { slug } = useParams();
  const t = TRADITIONS[slug];
  if (!t) return <Navigate to="/traditions" replace />;

  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="tradition-detail-page">
      <section className="relative py-24 lg:py-36 border-b border-indic-gold/15">
        <img src={t.img} alt={t.name} className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1021]/70 via-[#0B1021]/85 to-indic-indigo" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-8">
          <Link to="/traditions" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-indic-gold" data-testid="back-to-traditions"><ArrowLeft size={15} /> Traditions</Link>
          <span className="mt-8 block font-deva text-3xl" style={{ color: t.accent }}>{t.sanskrit}</span>
          <h1 className="mt-2 font-serif text-6xl lg:text-7xl font-semibold text-amber-50">{t.name}</h1>
          <p className="mt-2 font-serif italic text-2xl text-amber-100/80">{t.tagline}</p>
          <p className="mt-6 max-w-2xl text-lg text-slate-300/90 leading-relaxed">{t.intro}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {t.facts.map((f) => (
              <span key={f} className="rounded-full border border-indic-gold/30 px-4 py-1.5 text-xs font-sans text-indic-gold/90">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 space-y-12">
          {t.sections.map((s, i) => (
            <Reveal key={s.h} delay={0.04 * i}>
              <div className="relative pl-6 border-l-2" style={{ borderColor: t.accent + "66" }}>
                <h2 className="font-serif text-3xl font-semibold text-amber-50">{s.h}</h2>
                <p className="mt-3 text-lg text-slate-300/90 leading-relaxed">{s.p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {DEEP[slug] && (
        <section className="pb-8">
          <div className="mx-auto max-w-5xl px-4 sm:px-8 space-y-16">
            {DEEP[slug].map((mod, mi) => (
              <div key={mi} data-testid={`deep-module-${mi}`}>
                <Reveal>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-amber-50">{mod.title}</h2>
                  </div>
                  {mod.note && <p className="mt-2 text-slate-400 italic font-serif text-lg">{mod.note}</p>}
                  <div className="mt-6 h-px w-full" style={{ background: `linear-gradient(90deg, ${t.accent}55, transparent)` }} />
                </Reveal>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mod.items.map((it, ii) => (
                    <Reveal key={it.name} delay={(ii % 3) * 0.05}>
                      <div className="h-full rounded-sm border border-indic-card-border bg-indic-card p-5 hover:border-indic-gold/40 transition-colors">
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="font-serif text-xl text-amber-50">{it.name}</h3>
                          {it.script && <span className="font-deva text-lg shrink-0" style={{ color: t.accent }}>{it.script}</span>}
                        </div>
                        {it.sub && <div className="text-xs font-sans uppercase tracking-wider text-indic-gold/70 mt-1">{it.sub}</div>}
                        <p className="mt-3 text-sm text-slate-300/85 leading-relaxed">{it.text}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
            {TRADITION_SOURCES[slug] && (
              <p className="text-xs text-slate-500">
                Deep content integrated from the Mahakali Studios repository ·{" "}
                <a href={`https://${TRADITION_SOURCES[slug].site}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indic-sandstone hover:text-indic-gold" data-testid="tradition-source-link">
                  {TRADITION_SOURCES[slug].label} <ExternalLink size={11} />
                </a>
              </p>
            )}
          </div>
        </section>
      )}

      <section className="pb-24 pt-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <div className="rounded-sm border border-indic-card-border bg-indic-card p-8 text-center">
            <Sparkles className="mx-auto text-indic-gold" size={24} />
            <h3 className="mt-4 font-serif text-2xl text-amber-50">Go deeper</h3>
            <p className="mt-2 text-slate-300/85">Read the sources in the library, or ask INDIC Scholar about {t.name}.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/library" className="inline-flex items-center gap-2 rounded-sm border border-indic-gold/50 px-6 py-3 font-semibold text-amber-50 hover:bg-indic-gold/10 transition-colors" data-testid="tradition-library">Enter the Library</Link>
              <Link to="/scholar" className="inline-flex items-center gap-2 rounded-sm bg-indic-gold px-6 py-3 font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="tradition-scholar">Ask INDIC Scholar <ArrowRight size={15} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
